import { Card, CardContent, CardHeader, CardTitle } from "../Card"
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../Table"
import { Tooltip } from "../Tooltip"
import { api } from "../../../utils/api"
import { useDisplayCompanies } from "../../status/DisplayCompaniesByLifeCycleStage"
import LoadingOverlay from "../LoadingOverlay"
import { keyBy, merge, sortBy, startCase, values } from "lodash"
import { formatName } from "~/utils/namingUtils"

const SkusInformationOverview = () => {
  const tableSizes = api.healthCheck.getTableSizes
    .useQuery()
    .data?.filter((i) => i.orgId !== "template")
  const { data: skusWithNoForecast } = api.healthCheck.skusWithNoForecast.useQuery()
  const { data: skusMetadata } = api.healthCheck.skuMetadata.useQuery()
  const { data: manualForecast } = api.healthCheck.manualForecast.useQuery()
  const { data: activeSkus } = api.healthCheck.activeSkus.useQuery()
  const { data: shouldBeShownSkus } = api.healthCheck.sku.useQuery()
  const { data: ignoredSkus } = api.healthCheck.ignoredSkus.useQuery()
  const { data: npi } = api.healthCheck.npi.useQuery()
  const companies = useDisplayCompanies()

  const forecasts = tableSizes
    ?.filter((s) => s.tableName === "ForecastVariant")
    .map(({ orgId, counts }) => ({ orgId, forecasts: counts }))

  if (!companies) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Information about SKUs</CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingOverlay />
        </CardContent>
      </Card>
    )
  }
  const merged = merge(
    keyBy(forecasts, "orgId"),
    keyBy(skusWithNoForecast, "orgId"),
    keyBy(skusMetadata, "orgId"),
    keyBy(manualForecast, "orgId"),
    keyBy(activeSkus, "orgId"),
    keyBy(ignoredSkus, "orgId"),
    keyBy(shouldBeShownSkus, "orgId"),
    keyBy(npi, "orgId"),
    keyBy(
      companies.filter((comp) => comp.lastDataDate),
      "orgId",
    ),
  )
  const rowValues = sortBy(values(merged), "orgId").filter((v) =>
    companies.some((c) => c.orgId === v.orgId),
  )

  return (
    <Card className="h-fit">
      <CardHeader>
        <CardTitle>Information about SKUs</CardTitle>
      </CardHeader>
      <Table>
        <TableHeader>
          <TableRow className="text-left text-sm font-normal">
            <TableHead>Company</TableHead>
            <TableHead>Active</TableHead>
            <TableHead>Should be shown</TableHead>
            <TableHead>NPIs</TableHead>
            <TableHead>Ignored</TableHead>
            <TableHead>Without forecast</TableHead>
            <TableHead>Without {formatName("sale", true)}</TableHead>
            <TableHead>Phasing out</TableHead>
            <TableHead>Phased out</TableHead>
            <TableHead>Manual forecast</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rowValues.map((row) => (
            <TableRow key={row.orgId}>
              <TableCell>
                <Tooltip title={`Org ID: ${row.orgId}`}>
                  <div>{startCase(row.orgId)}</div>
                </Tooltip>
              </TableCell>
              <TableCell>{activeSkus ? (row.activeSkus ?? "---") : "Loading"}</TableCell>
              <TableCell>
                {shouldBeShownSkus ? (row.shouldBeShownSkus ?? "---") : "Loading"}
              </TableCell>
              <TableCell>{npi ? (row.npi ?? "---") : "Loading"}</TableCell>
              <TableCell>{npi ? (row.ignoredSkus ?? "---") : "Loading"}</TableCell>
              <TableCell>
                {skusWithNoForecast ? (row.skusWithNoForecast ?? "---") : "Loading"}
              </TableCell>
              <TableCell>
                {skusWithNoForecast ? (row.skusWithNoSales ?? "---") : "Loading"}
              </TableCell>
              <TableCell>{skusMetadata ? (row.phasingOut ?? "---") : "Loading"}</TableCell>
              <TableCell>{skusMetadata ? (row.phasedOut ?? "---") : "Loading"}</TableCell>
              <TableCell>{skusMetadata ? (row.manualForecast ?? "---") : "Loading"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}

export default SkusInformationOverview
